// ZoneEditor — Wansight-style sidebar layout.
//
//   ┌─ Page header ───────────────────────────────────────────┐
//   │  Icon  IP Zone Configuration                             │
//   ├─────────────────┬───────────────────────────────────────┤
//   │ ▾ IP Addresses  │    Settings for <selected prefix>     │
//   │ IP/Mask: [🔍  ] │                                        │
//   │ ▾ ☁ /0          │    [param grid]                       │
//   │   └─ ☁ 10/8     │                                        │
//   │   └─ ☁ 2001::/32│                                        │
//   │ [+ Add][Bulk][X]│                                        │
//   ├─────────────────┤                                        │
//   │ ▾ IP Groups     │                                        │
//   │ IP Group:[🔍 ]  │                                        │
//   │ 🏷 default ●    │                                        │
//   │ 🏷 production   │                                        │
//   │ [+ New][🗑 Del] │                                        │
//   └─────────────────┴───────────────────────────────────────┘
import { useEffect, useMemo, useState } from "react";
import { useRole } from "../contexts/AuthContext";
import { useIPZone } from "../contexts/IPZoneContext";
import {
  Alert,
  AutoComplete,
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ApartmentOutlined,
  CloudOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ProfileOutlined,
  TagOutlined,
} from "@ant-design/icons";
import {
  api,
  type IPAddrWriteReq,
  type Zone,
  type ZoneNode,
} from "../api";

function safeFilename(name: string): string {
  const cleaned = (name || "zone")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned || "zone";
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ZoneEditor() {
  const { isAdmin, isOperator } = useRole();
  const {
    zones,
    zonesLoading: _zlLoading, // unused
    activeZoneID,
    setActiveZoneID,
    activeNodes: nodes,
    nodesLoading: loading,
    refresh,
    refreshNodes,
  } = useIPZone();
  void _zlLoading;
  const activeZone = activeZoneID;
  const setActiveZone = setActiveZoneID;
  const [selectedID, setSelectedID] = useState<number | null>(null);

  // Filters for the two sidebars
  const [prefixSearch, setPrefixSearch] = useState("");
  const [zoneSearch, setZoneSearch] = useState("");

  // Collapsible panels
  const [prefixesOpen, setPrefixesOpen] = useState(true);
  const [groupsOpen, setGroupsOpen]     = useState(true);
  const [rootExpanded, setRootExpanded] = useState(true);

  // Modals
  const [addOpen, setAddOpen] = useState(false);
  const [editNode, setEditNode] = useState<ZoneNode | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [zoneCreateOpen, setZoneCreateOpen] = useState(false);
  const [zoneRenameOpen, setZoneRenameOpen] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  // When active zone or its nodes change, drop the selected-id if stale.
  useEffect(() => {
    if (selectedID && !nodes.some((n) => n.id === selectedID)) {
      setSelectedID(null);
    }
  }, [nodes, selectedID]);

  const activeZoneMeta = useMemo(
    () => zones.find((z) => z.id === activeZone) ?? null,
    [zones, activeZone]
  );

  const selected = useMemo(
    () => nodes.find((n) => n.id === selectedID) ?? null,
    [nodes, selectedID]
  );

  const filteredNodes = useMemo(() => {
    const q = prefixSearch.trim().toLowerCase();
    if (!q) return nodes;
    return nodes.filter(
      (n) =>
        n.prefix.toLowerCase().includes(q) ||
        (n.description ?? "").toLowerCase().includes(q)
    );
  }, [nodes, prefixSearch]);

  // Export the active zone's prefixes as a plain CIDR list (.txt), one per
  // line — the exact format the Bulk import accepts, so it round-trips.
  const exportZone = () => {
    if (activeZone == null || !activeZoneMeta) {
      message.warning("Select a zone first");
      return;
    }
    const lines = nodes.map((n) => n.prefix);
    if (lines.length === 0) {
      message.warning("This zone has no prefixes to export");
      return;
    }
    downloadText(
      safeFilename(activeZoneMeta.name) + ".txt",
      lines.join("\n") + "\n"
    );
    message.success(
      `Exported ${lines.length} prefix(es) from "${activeZoneMeta.name}"`
    );
  };

  // Export every zone's prefixes into one .txt, each zone preceded by a
  // "# <name>" comment header. Bulk import skips comment/blank lines, so any
  // section pastes straight back in.
  const exportAllZones = async () => {
    if (zones.length === 0) {
      message.warning("No zones to export");
      return;
    }
    setExportingAll(true);
    try {
      const sections: string[] = [];
      let total = 0;
      for (const z of zones) {
        const zNodes = await api.zoneNodes(z.id);
        const lines = zNodes.map((n) => n.prefix);
        total += lines.length;
        sections.push(`# ${z.name} (${lines.length})\n${lines.join("\n")}`);
      }
      downloadText("ip-ranges-all.txt", sections.join("\n\n") + "\n");
      message.success(
        `Exported ${total} prefix(es) across ${zones.length} zone(s)`
      );
    } catch (e) {
      message.error(
        "Export failed: " + (e instanceof Error ? e.message : String(e))
      );
    } finally {
      setExportingAll(false);
    }
  };

  const filteredZones = useMemo(() => {
    const q = zoneSearch.trim().toLowerCase();
    if (!q) return zones;
    return zones.filter((z) => z.name.toLowerCase().includes(q));
  }, [zones, zoneSearch]);

  async function handleDeleteZone(force = false) {
    if (activeZone == null) return;
    try {
      await api.deleteZone(activeZone, force);
      message.success("Zone deleted");
      setSelectedID(null);
      setActiveZone(null);
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Zone still attached to sensors → offer one-click force detach + delete.
      if (!force && /still used by/i.test(msg)) {
        Modal.confirm({
          title: "Zone still attached to sensors",
          content: (
            <span>
              {msg}
              <br />
              <br />
              Force delete detaches this zone from those sensors, then deletes it.
            </span>
          ),
          okText: "Detach from all & delete",
          okButtonProps: { danger: true },
          onOk: () => handleDeleteZone(true),
        });
        return;
      }
      message.error(msg);
    }
  }

  async function handleDeletePrefix() {
    if (activeZone == null || !selected) return;
    try {
      await api.deleteZoneNode(activeZone, selected.id);
      setSelectedID(null);
      refreshNodes();
    } catch (e) {
      message.error(String(e));
    }
  }

  // ---------------------------------------------------------------------
  return (
    <>
      {/* Page header */}
      <div className="page-header-band">
        <div className="ph-left">
          <div className="ph-icon">
            <ApartmentOutlined />
          </div>
          <div>
            <div className="ph-title">IP Zone Configuration</div>
            <div className="ph-subtitle">
              Define accounting &amp; graphing rules per CIDR prefix
            </div>
          </div>
        </div>
      </div>

      <Row gutter={16}>
        {/* ── LEFT COLUMN: IP Addresses + IP Groups panels ─────────── */}
        <Col xs={24} md={9} lg={7}>
          {/* ── IP Addresses panel ─────────────────────────────────── */}
          <div className="wsl-panel">
            <button
              type="button"
              className="wsl-panel-header"
              onClick={() => setPrefixesOpen((o) => !o)}
            >
              <span className="wsl-panel-title">IP Addresses</span>
              <span className="wsl-panel-chip">
                {filteredNodes.length}
                {prefixSearch && filteredNodes.length !== nodes.length
                  ? ` / ${nodes.length}`
                  : ""}
              </span>
              <span className={`wsl-collapse ${prefixesOpen ? "open" : ""}`}>▾</span>
            </button>

            {prefixesOpen && (
              <>
                <div className="wsl-panel-search">
                  <span className="wsl-search-label">IP / Mask:</span>
                  <Input
                    allowClear
                    placeholder="Search …"
                    size="small"
                    value={prefixSearch}
                    onChange={(e) => setPrefixSearch(e.target.value)}
                    style={{ flex: 1, fontFamily: "monospace", fontSize: 12 }}
                  />
                </div>

                <div className="wsl-tree">
                  {loading ? (
                    <div className="wsl-empty">Loading…</div>
                  ) : activeZone == null ? (
                    <div className="wsl-empty">Pick a zone below ↓</div>
                  ) : (
                    <>
                      {/* Root /0 */}
                      <div
                        className="wsl-tree-row wsl-tree-root"
                        onClick={() => setRootExpanded((e) => !e)}
                      >
                        <span className={`wsl-caret ${rootExpanded ? "open" : ""}`}>
                          ▾
                        </span>
                        <FolderOpenOutlined className="wsl-tree-icon root" />
                        <span className="wsl-tree-label">
                          /0
                          {activeZoneMeta && (
                            <span className="wsl-tree-meta">
                              {" "}
                              {activeZoneMeta.name}
                            </span>
                          )}
                        </span>
                      </div>

                      {rootExpanded && filteredNodes.length === 0 && (
                        <div className="wsl-empty">
                          {prefixSearch
                            ? `No match for "${prefixSearch}"`
                            : "No prefixes — use Add or Bulk below"}
                        </div>
                      )}

                      {rootExpanded &&
                        filteredNodes.map((n) => {
                          const active = n.id === selectedID;
                          const isV6 = n.prefix.includes(":");
                          return (
                            <div
                              key={n.id}
                              className={`wsl-tree-row wsl-tree-leaf ${
                                active ? "active" : ""
                              }`}
                              onClick={() => setSelectedID(n.id)}
                              onDoubleClick={() => setEditNode(n)}
                              title={n.description || undefined}
                            >
                              <span className="wsl-leaf-line">└</span>
                              <CloudOutlined
                                className={`wsl-tree-icon ${isV6 ? "v6" : "v4"}`}
                              />
                              <span className="wsl-leaf-prefix">{n.prefix}</span>
                              {n.description && (
                                <span className="wsl-leaf-desc">
                                  {n.description}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>

                {/* Actions for prefixes */}
                <div className="wsl-panel-actions">
                  {isOperator && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<PlusOutlined />}
                      disabled={activeZone == null}
                      onClick={() => setAddOpen(true)}
                    >
                      Add
                    </Button>
                  )}
                  {isOperator && (
                    <Button
                      size="small"
                      icon={<ProfileOutlined />}
                      disabled={activeZone == null}
                      onClick={() => setBulkOpen(true)}
                    >
                      Bulk
                    </Button>
                  )}
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "zone",
                          label: activeZoneMeta
                            ? `This zone (${activeZoneMeta.name})`
                            : "This zone",
                          disabled: activeZone == null,
                          onClick: exportZone,
                        },
                        {
                          key: "all",
                          label: "All zones",
                          onClick: exportAllZones,
                        },
                      ],
                    }}
                  >
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={exportingAll}
                    >
                      Export
                    </Button>
                  </Dropdown>
                  {isAdmin && (
                    <Popconfirm
                      title={selected ? `Delete ${selected.prefix}?` : ""}
                      disabled={!selected}
                      onConfirm={handleDeletePrefix}
                    >
                      <Tooltip
                        title={!selected ? "Select a prefix first" : undefined}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={!selected}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── IP Groups panel ────────────────────────────────────── */}
          <div className="wsl-panel">
            <button
              type="button"
              className="wsl-panel-header"
              onClick={() => setGroupsOpen((o) => !o)}
            >
              <span className="wsl-panel-title">IP Groups</span>
              <span className="wsl-panel-chip">
                {filteredZones.length}
                {zoneSearch && filteredZones.length !== zones.length
                  ? ` / ${zones.length}`
                  : ""}
              </span>
              <span className={`wsl-collapse ${groupsOpen ? "open" : ""}`}>▾</span>
            </button>

            {groupsOpen && (
              <>
                <div className="wsl-panel-search">
                  <span className="wsl-search-label">IP Group:</span>
                  <Input
                    allowClear
                    placeholder="Search …"
                    size="small"
                    value={zoneSearch}
                    onChange={(e) => setZoneSearch(e.target.value)}
                    style={{ flex: 1, fontSize: 12 }}
                  />
                </div>

                <div className="wsl-group-list">
                  {filteredZones.length === 0 ? (
                    <div className="wsl-empty">
                      {zoneSearch
                        ? `No match for "${zoneSearch}"`
                        : "No zones yet — create one below"}
                    </div>
                  ) : (
                    filteredZones.map((z) => {
                      const active = z.id === activeZone;
                      return (
                        <div
                          key={z.id}
                          className={`wsl-group-item ${active ? "active" : ""}`}
                          onClick={() => {
                            setActiveZone(z.id);
                            setSelectedID(null);
                          }}
                          title={`Zone #${z.id}`}
                        >
                          <TagOutlined className="wsl-group-icon" />
                          <span className="wsl-group-name">{z.name}</span>
                          {z.has_ipv6 && (
                            <Tag color="purple" className="wsl-group-v6">
                              v6
                            </Tag>
                          )}
                          {active && <span className="wsl-group-dot" />}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="wsl-panel-actions">
                  {isOperator && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setZoneCreateOpen(true)}
                    >
                      New zone
                    </Button>
                  )}
                  {isOperator && (
                    <Tooltip
                      title={activeZone == null ? "Pick a zone first" : undefined}
                    >
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        disabled={activeZone == null}
                        onClick={() => setZoneRenameOpen(true)}
                      >
                        Rename
                      </Button>
                    </Tooltip>
                  )}
                  {isAdmin && (
                    <Popconfirm
                      title={`Delete zone "${activeZoneMeta?.name}"?`}
                      description={
                        <span style={{ fontSize: 12 }}>
                          Removes the zone and all <b>{nodes.length} prefix(es)</b>.
                          Cannot be undone.
                        </span>
                      }
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      placement="topRight"
                      disabled={activeZone == null}
                      onConfirm={() => handleDeleteZone()}
                    >
                      <Tooltip
                        title={activeZone == null ? "Pick a zone first" : undefined}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={activeZone == null}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Popconfirm>
                  )}
                </div>
              </>
            )}
          </div>
        </Col>

        {/* ── RIGHT COLUMN: settings panel ─────────────────────────── */}
        <Col xs={24} md={15} lg={17}>
          <div className="wsl-right-card">
            {selected ? (
              <Tabs
                size="small"
                style={{ padding: "8px 14px 14px" }}
                items={[
                  {
                    key: "settings",
                    label: "Settings",
                    children: (
                      <SettingsPanel
                        node={selected}
                        canEdit={isOperator}
                        onEdit={() => setEditNode(selected)}
                      />
                    ),
                  },
                  {
                    key: "comments",
                    label: "Comments",
                    children: (
                      <div style={{ padding: 12, color: "#94a3b8", fontSize: 12 }}>
                        Comments for {selected.prefix} — not implemented yet.
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <div className="zone-right-empty">
                <div className="zone-right-empty-icon">
                  <ApartmentOutlined />
                </div>
                <div className="zone-right-empty-title">No prefix selected</div>
                <div className="zone-right-empty-sub">
                  Pick a prefix on the left to view &amp; edit its settings
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>

      <NodeForm
        open={addOpen || editNode != null}
        onClose={() => {
          setAddOpen(false);
          setEditNode(null);
        }}
        onSaved={() => refreshNodes()}
        zoneID={activeZone}
        node={editNode}
      />

      <BulkForm
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSaved={() => refreshNodes()}
        zoneID={activeZone}
      />

      <ZoneCreateForm
        open={zoneCreateOpen}
        onClose={() => setZoneCreateOpen(false)}
        onSaved={(id) => {
          setSelectedID(null);
          setActiveZone(id);
          refresh();
        }}
      />

      <ZoneRenameForm
        open={zoneRenameOpen}
        onClose={() => setZoneRenameOpen(false)}
        onSaved={() => refresh()}
        zone={activeZoneMeta}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Right-panel settings content
// ---------------------------------------------------------------------------
function SettingsPanel({
  node,
  canEdit,
  onEdit,
}: {
  node: ZoneNode;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const isV6 = node.prefix.includes(":");
  return (
    <div>
      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
          paddingBottom: 10,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <CloudOutlined
          style={{
            fontSize: 18,
            color: isV6 ? "#8b5cf6" : "#10b981",
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 15,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            {node.prefix}
          </div>
          {node.description && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {node.description}
            </div>
          )}
        </div>
        {canEdit && (
          <Button
            size="small"
            type="primary"
            icon={<EditOutlined />}
            onClick={onEdit}
          >
            Edit
          </Button>
        )}
      </div>

      {/* params */}
      <div className="zone-param-grid">
        <div className="zone-param-row">
          <div className="zone-param-label">
            IP Accounting
            <span className="zone-param-help">Include in flow aggregates</span>
          </div>
          <div className="zone-param-value">
            <Tag color={node.accounting ? "green" : "default"}>
              {node.accounting ? "Enabled" : "Disabled"}
            </Tag>
          </div>
        </div>

        <div className="zone-param-row">
          <div className="zone-param-label">
            IP Graphing
            <span className="zone-param-help">Per-IP time series in InfluxDB</span>
          </div>
          <div className="zone-param-value">
            <Tag color={node.graphs ? "blue" : "default"}>
              {node.graphs ? "Enabled" : "Disabled"}
            </Tag>
          </div>
        </div>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16 }}
        message="Storage impact"
        description={
          <span style={{ fontSize: 12 }}>
            Accounting grows at roughly <b>~210 MB/day per /16</b> in MariaDB. Graph
            rows scale with prefix size in InfluxDB.
          </span>
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modals (unchanged except minor polish)
// ---------------------------------------------------------------------------

function NodeForm({
  open,
  onClose,
  onSaved,
  zoneID,
  node,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  zoneID: number | null;
  node: ZoneNode | null;
}) {
  const [form] = Form.useForm<IPAddrWriteReq>();
  const [busy, setBusy] = useState(false);
  // Cached list of existing ipgroup names — feeds the AutoComplete so
  // the operator can pick a previous label instead of retyping it.
  // Backed by /api/v1/ipgroups; reloaded each time the modal opens so a
  // group created from another tab shows up without a hard refresh.
  const [groupOptions, setGroupOptions] = useState<{ value: string }[]>([]);

  useEffect(() => {
    if (!open) return;
    if (node) {
      form.setFieldsValue({
        prefix: node.prefix,
        description: node.description,
        graphs: node.graphs,
        accounting: node.accounting,
        ip_group: node.ip_group ?? "",
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ graphs: true, accounting: true, ip_group: "" });
    }
    api
      .ipgroups()
      .then((rows) => setGroupOptions(rows.map((r) => ({ value: r.name }))))
      .catch(() => setGroupOptions([])); // best-effort; field still works typed-only
  }, [open, node, form]);

  async function onOk() {
    if (zoneID == null) return;
    try {
      const v = await form.validateFields();
      setBusy(true);
      if (node) {
        await api.updateZoneNode(zoneID, node.id, v);
      } else {
        await api.createZoneNode(zoneID, v);
      }
      message.success(node ? "Updated" : "Created");
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={node ? `Edit ${node.prefix}` : "Add prefix"}
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      okText="Save"
    >
      <Form<IPAddrWriteReq> form={form} layout="vertical">
        <Form.Item
          name="prefix"
          label="CIDR prefix"
          rules={[{ required: true, message: "e.g. 10.0.0.0/8 or 2001:db8::/32" }]}
        >
          <Input placeholder="10.0.0.0/8" style={{ fontFamily: "monospace" }} />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input placeholder="customer name, region, …" />
        </Form.Item>
        <Form.Item
          name="ip_group"
          label="IP Group"
          tooltip="Wansight-style customer/service label. Type a new name or pick an existing one — the dashboard's Top Talker widget shows this label next to every IP that falls inside this prefix. Empty = no group (widget falls back to the zone name)."
        >
          <AutoComplete
            options={groupOptions}
            placeholder="e.g. Customer-A — leave blank to clear"
            allowClear
            filterOption={(input, opt) =>
              (opt?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="graphs"
              label="Graphs"
              valuePropName="checked"
              extra="Store per-IP time series in InfluxDB"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="accounting"
              label="Accounting"
              valuePropName="checked"
              extra="Include in flow aggregates"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function BulkForm({
  open,
  onClose,
  onSaved,
  zoneID,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  zoneID: number | null;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setText("");
  }, [open]);

  async function onOk() {
    if (zoneID == null) return;
    const prefixes = text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter((s) => s && !s.startsWith("#"));
    if (prefixes.length === 0) {
      message.warning("No prefixes to insert");
      return;
    }
    setBusy(true);
    try {
      const r = await api.bulkZoneNodes(zoneID, prefixes);
      message.success(`Inserted ${r.inserted} prefixes`);
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="Bulk paste CIDR list"
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      okText="Insert"
      width={620}
    >
      <p style={{ color: "#64748b", fontSize: 12 }}>
        One CIDR per line. Lines starting with <code>#</code> are ignored. All inserted
        rows default to <b>graphs</b>=on, <b>accounting</b>=on.
      </p>
      <Input.TextArea
        rows={14}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"10.0.0.0/8\n172.16.0.0/12\n192.168.0.0/16\n# comment line"}
        style={{ fontFamily: "monospace" }}
      />
    </Modal>
  );
}

// ZoneRenameForm — renames the currently-active zone and toggles its
// has_ipv6 flag. Both fields are pushed via PUT /api/v1/zone/{id}.
function ZoneRenameForm({
  open,
  onClose,
  onSaved,
  zone,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  zone: Zone | null;
}) {
  const [form] = Form.useForm<{ name: string; has_ipv6: boolean }>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && zone) {
      form.setFieldsValue({ name: zone.name, has_ipv6: zone.has_ipv6 });
    }
  }, [open, zone, form]);

  async function onOk() {
    if (!zone) return;
    try {
      const v = await form.validateFields();
      const name = v.name.trim();
      if (!name) {
        message.error("Name cannot be empty");
        return;
      }
      setBusy(true);
      await api.updateZone(zone.id, name, v.has_ipv6);
      message.success("Zone updated");
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={zone ? `Rename zone #${zone.id}` : "Rename zone"}
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      okText="Save"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input autoFocus placeholder="default, datacenter-hn, …" />
        </Form.Item>
        <Form.Item
          name="has_ipv6"
          label="IPv6"
          valuePropName="checked"
          extra="Tick if this zone holds IPv6 prefixes"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ZoneCreateForm({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (newID: number) => void;
}) {
  const [form] = Form.useForm<{ name: string; has_ipv6: boolean }>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ has_ipv6: false });
    }
  }, [open, form]);

  async function onOk() {
    try {
      const v = await form.validateFields();
      setBusy(true);
      const r = await api.createZone(v.name, v.has_ipv6);
      message.success("Zone created");
      onSaved(r.id);
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title="New IP Zone"
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      okText="Create"
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="default, datacenter-hn, …" />
        </Form.Item>
        <Form.Item
          name="has_ipv6"
          label="IPv6"
          valuePropName="checked"
          extra="Tick if this zone will hold IPv6 prefixes"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
